import { useMutation } from '@tanstack/react-query';

import axios from 'axios';
import { MutationConfig, queryClient } from 'lib/react-query';

import { SnapshotMonitor, UpdateSnapshotMonitorDto } from 'types';

export const updateMonitor = ({
  data,
  snapshotId,
  monitorId,
}: {
  data: UpdateSnapshotMonitorDto;
  snapshotId: number;
  monitorId: number;
}): Promise<SnapshotMonitor> => {
  return axios.put(`/api/snapshots/${snapshotId}/monitors/${monitorId}`, data);
};

type UseUpdateSnapshotOptions = {
  snapshotId: number;
  config?: MutationConfig<typeof updateMonitor>;
};

export const useUpdateMonitor = ({
  config,
  snapshotId,
}: UseUpdateSnapshotOptions) => {
  return useMutation({
    onMutate: async (updatingMonitor) => {
      await queryClient.cancelQueries([
        'snapshot',
        snapshotId,
        'monitors',
        updatingMonitor.monitorId,
      ]);

      const previousMonitor = queryClient.getQueryData<SnapshotMonitor>([
        'snapshot',
        snapshotId,
        'monitors',
        updatingMonitor.monitorId,
      ]);

      queryClient.setQueryData(
        ['snapshot', snapshotId, 'monitors', updatingMonitor.monitorId],
        {
          ...previousMonitor,
          ...updatingMonitor.data,
          id: updatingMonitor.monitorId,
        }
      );

      return previousMonitor;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['snapshot', snapshotId]);
    },
    ...config,
    mutationFn: updateMonitor,
  });
};
